import { zodResolver } from '@hookform/resolvers/zod';
import {
  MAX_MIN_DESCRIPTION_LENGTH,
  updateProductConfigurationSchema,
  type UpdateProductConfigurationInput,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';
import { SettingsSection } from '../coda/SettingsSection';

export interface MinDescriptionSectionProps {
  value: number;
  busy?: boolean;
  error?: string | null;
  onSave: (input: UpdateProductConfigurationInput) => void;
}

/** Lunghezza minima della descrizione che il form dell'intake applica prima dell'invio (contratto §7). */
export function MinDescriptionSection({ value, busy = false, error = null, onSave }: MinDescriptionSectionProps) {
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<UpdateProductConfigurationInput>({
    resolver: zodResolver(updateProductConfigurationSchema),
    defaultValues: { minDescriptionLength: value },
  });

  return (
    <SettingsSection
      id="min-description-title"
      title="Descrizione"
      description="Quanti caratteri deve avere la descrizione prima che il form del prodotto accetti l'invio."
    >
      <form onSubmit={handleSubmit(onSave)} noValidate>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'flex-start' }}>
          <Controller
            name="minDescriptionLength"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value === '' ? '' : Number(event.target.value))}
                type="number"
                label="Lunghezza minima della descrizione"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? `Da 0 a ${MAX_MIN_DESCRIPTION_LENGTH} caratteri.`}
                slotProps={{ htmlInput: { min: 0, max: MAX_MIN_DESCRIPTION_LENGTH, step: 1 } }}
                sx={{ width: { sm: 320 } }}
              />
            )}
          />
          <Button type="submit" variant="contained" disabled={busy || !isDirty} sx={{ mt: { sm: 2 } }}>
            Salva
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </form>
    </SettingsSection>
  );
}
